import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Plus, X } from 'lucide-react';
import { useBooking } from '../../contexts/BookingContext';
import { useAuth } from '../../contexts/AuthContext';
import { formatPriceFCFA, formatDuration } from '../../utils/formatters';
import { useClosedDates } from '../../hooks/useClosedDates';
import ServiceSelector from '../../components/Booking/ServiceSelector';
import WeekView from '../../components/Booking/WeekView';
import Calendar from '../../components/Booking/Calendar';
import DaySlots from '../../components/Booking/DaySlots';
import AuthModal from '../../components/Auth/AuthModal';
import './Booking.css';

const STEPS = ['Prestations', 'Créneau', 'Récap', 'Paiement'];

const Booking = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    selectedServices,
    selectedSalon,
    selectedDate: contextDate,
    selectedTime: contextTime,
    removeService,
    getTotalDuration,
    getTotalPrice,
    setSelectedDate: setContextDate,
    setSelectedTime: setContextTime,
  } = useBooking();

  const [showServiceSelector, setShowServiceSelector] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(contextDate || null);
  const [selectedTime, setSelectedTime] = useState(contextTime || null);
  const [viewMode, setViewMode] = useState('week');

  // Restaurer date/heure depuis le contexte après un refresh
  useEffect(() => {
    if (contextDate && !selectedDate) setSelectedDate(contextDate);
    if (contextTime && !selectedTime) setSelectedTime(contextTime);
  }, [contextDate, contextTime, selectedDate, selectedTime]);

  const {
    closedDates,
    isDateDisabled: isExceptionallyClosed,
    getClosureMessage,
  } = useClosedDates(selectedSalon?.id);

  // La durée totale conditionne les créneaux disponibles : toute modification
  // des prestations invalide l'heure déjà choisie.
  const servicesKey = selectedServices.map((s) => s.id).join(',');
  const lastServicesKeyRef = useRef(servicesKey);
  useEffect(() => {
    if (lastServicesKeyRef.current === servicesKey) return;
    lastServicesKeyRef.current = servicesKey;
    setSelectedTime(null);
    setContextTime(null);
  }, [servicesKey, setContextTime]);

  if (selectedServices.length === 0) {
    return (
      <div className="booking-empty">
        <h2>Aucun service sélectionné</h2>
        <p>Choisissez d’abord une prestation sur la fiche salon.</p>
        <button type="button" onClick={() => navigate(-1)} className="btn-continue">
          Retour
        </button>
      </div>
    );
  }

  const handleRemoveService = (serviceId) => {
    removeService(serviceId);
    if (selectedServices.length === 1) navigate(-1);
  };

  const handleContinue = () => {
    if (!selectedDate || !selectedTime) {
      alert('Veuillez choisir une date et une heure');
      return;
    }
    if (isExceptionallyClosed(selectedDate)) {
      alert('Ce jour est en fermeture exceptionnelle. Veuillez choisir une autre date.');
      return;
    }
    setContextDate(selectedDate);
    setContextTime(selectedTime);
    if (!user) {
      setShowAuthModal(true);
    } else {
      navigate('/booking/recap');
    }
  };

  const canContinue = !!(selectedDate && selectedTime);

  return (
    <div className="booking-container">
      <div className="bk-hero">
        <div className="bk-hero-glow" />
        <div className="bk-hero-inner">
          <button type="button" className="bk-back" onClick={() => navigate(-1)} aria-label="Retour">
            <ChevronLeft size={22} />
          </button>
          <div className="bk-hero-titles">
            <h1>Réservation</h1>
            <p>{selectedSalon?.name || 'Choisissez votre créneau'}</p>
          </div>
          <div className="bk-back-spacer" />
        </div>
        <div className="bk-steps">
          {STEPS.map((label, i) => (
            <div key={label} className={`bk-step ${i <= 1 ? 'active' : ''} ${i === 1 ? 'current' : ''}`}>
              <span className="bk-step-num">{i + 1}</span>
              <span className="bk-step-label">{label}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="booking-content">
        <div className="booking-section">
          <div className="section-header">
            <h2>Services sélectionnés</h2>
            <button
              type="button"
              className="btn-add-service"
              onClick={() => setShowServiceSelector(true)}
            >
              <Plus size={16} />
              Ajouter
            </button>
          </div>

          <div className="selected-services-list">
            {selectedServices.map((service, index) => (
              <div key={service.id} className="selected-service-card">
                <div className="service-number">{index + 1}</div>
                <div className="service-info">
                  <h3>{service.name}</h3>
                  <p className="service-details">
                    {formatDuration(service.duration)} · {formatPriceFCFA(service.price)}
                  </p>
                </div>
                <button
                  type="button"
                  className="btn-remove"
                  onClick={() => handleRemoveService(service.id)}
                  aria-label="Retirer"
                >
                  <X size={16} />
                </button>
              </div>
            ))}
          </div>

          <div className="booking-total">
            <div className="total-row">
              <span>Durée totale</span>
              <strong>{formatDuration(getTotalDuration())}</strong>
            </div>
            <div className="total-row total-price">
              <span>Prix total</span>
              <strong>{formatPriceFCFA(getTotalPrice())}</strong>
            </div>
          </div>
        </div>

        <div className="booking-section">
          <div className="booking-section-header">
            <h2>Choisir un créneau</h2>
            <div className="view-toggle">
              <button
                type="button"
                className={`toggle-btn ${viewMode === 'week' ? 'active' : ''}`}
                onClick={() => setViewMode('week')}
              >
                Semaine
              </button>
              <button
                type="button"
                className={`toggle-btn ${viewMode === 'month' ? 'active' : ''}`}
                onClick={() => setViewMode('month')}
              >
                Mois
              </button>
            </div>
          </div>

          {viewMode === 'week' && (
            <WeekView
              salonId={selectedSalon?.id}
              selectedDate={selectedDate}
              selectedTime={selectedTime}
              closedDates={closedDates}
              isExceptionallyClosed={isExceptionallyClosed}
              onSelectSlot={(date, time) => {
                setSelectedDate(date);
                setSelectedTime(time);
              }}
            />
          )}

          {viewMode === 'month' && (
            <>
              <div className="calendar-section">
                <h3>Date</h3>
                <Calendar
                  selectedDate={selectedDate}
                  closedDates={closedDates}
                  isExceptionallyClosed={isExceptionallyClosed}
                  onSelectDate={(date) => {
                    setSelectedDate(date);
                    setSelectedTime(null);
                  }}
                />
              </div>
              <div className="month-slots-section">
                <h3>Créneaux disponibles</h3>
                <DaySlots
                  salonId={selectedSalon?.id}
                  selectedDate={selectedDate}
                  selectedTime={selectedTime}
                  isExceptionallyClosed={isExceptionallyClosed}
                  getClosureMessage={getClosureMessage}
                  onSelectTime={setSelectedTime}
                />
              </div>
            </>
          )}
        </div>
      </div>

      <div className="bk-sticky-footer">
        <div className="bk-sticky-inner">
          <div className="bk-sticky-meta">
            {canContinue ? (
              <>
                <span className="bk-sticky-slot">
                  {selectedDate} · {selectedTime}
                </span>
                <strong>{formatPriceFCFA(getTotalPrice())}</strong>
              </>
            ) : (
              <span className="bk-sticky-hint">Sélectionnez une date et une heure</span>
            )}
          </div>
          <button
            type="button"
            className="btn-continue"
            onClick={handleContinue}
            disabled={!canContinue}
          >
            Continuer
          </button>
        </div>
      </div>

      {showServiceSelector && (
        <ServiceSelector
          salonId={selectedSalon?.id}
          onClose={() => setShowServiceSelector(false)}
        />
      )}

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSuccess={() => {
          setShowAuthModal(false);
          navigate('/booking/recap');
        }}
      />
    </div>
  );
};

export default Booking;
